import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Loader2 } from 'lucide-react';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (teamMemberId: string) => void;
  selectedContactCount: number;
  isLoading?: boolean;
};

const AssignImportedContactsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onAssign,
  selectedContactCount,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const { data: teamMembers = [] } = useGetTeamMembersQuery();

  if (!isOpen) return null;

  // Filter to only ACTIVE members with role SALES or CUSTOMER_SUCCESS
  const eligibleMembers = teamMembers.filter(
    (m) =>
      m.status === 'ACTIVE' &&
      (m.role === 'Account Executive' || m.role === 'Customer Success')
  );

  const filteredMembers = eligibleMembers.filter((m) => {
    const name = `${m.firstName} ${m.lastName}`;
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getRoleBadge = (role: string) => {
    if (role === 'Account Executive') {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          AE
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        CSM
      </span>
    );
  };

  const handleAssign = () => {
    if (selectedMemberId) {
      onAssign(selectedMemberId);
    }
  };

  const handleClose = () => {
    setSelectedMemberId(null);
    setSearchTerm('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('promoters.assignModal.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('promoters.assignModal.description', { count: selectedContactCount })}
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('promoters.assignModal.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filteredMembers.length > 0 ? (
            <div className="space-y-2">
              {filteredMembers.map((member) => {
                const name = `${member.firstName} ${member.lastName}`;
                const isSelected = selectedMemberId === member.id;
                const initials = `${member.firstName[0] ?? ''}${member.lastName[0] ?? ''}`.toUpperCase();
                return (
                  <div
                    key={member.id}
                    onClick={() => setSelectedMemberId(member.id)}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#4339A0] bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-700 flex-shrink-0">
                        {member.avatar ? (
                          <img src={member.avatar} alt={name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900 truncate">{name}</h4>
                          {getRoleBadge(member.role)}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{member.email}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'border-[#4339A0] bg-[#4339A0]' : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm
                  ? t('promoters.assignModal.noSearchResults')
                  : t('promoters.assignModal.noEligible')}
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedMemberId || isLoading}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
              selectedMemberId && !isLoading
                ? 'bg-[#4339A0] hover:bg-[#3d2f8f] text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('promoters.assignModal.assigning')}</span>
              </>
            ) : (
              <span>{t('promoters.assignModal.assign')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignImportedContactsModal;
